// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// A lista de pedidos — tipos e lógica pura
// ============================================================================
//
// 📗 fonte 5.3.3.1. O que aqui está é a **leitura** da lista: como se ordena, que
// marcadores tem, que estados são legítimos. O desenho está em `Pedidos.tsx`.
//
// Separado por ser a parte que se testa sem DOM — e a ordenação da fila é
// precisamente o que erra em silêncio: uma ordem errada não parece mal, só faz o
// planeador trabalhar pela ordem errada.
// ============================================================================

/** Estados da máquina especificada em `modelo-de-dados-proposta` §1.2. */
export type EstadoPedido =
  | 'recebido'
  | 'em_validacao'
  | 'aguarda_informacao'
  | 'aguarda_aprovacao'
  | 'novo'
  | 'reservado'
  | 'confirmado'
  | 'executado'
  | 'cancelado';

export interface PontoPedido {
  readonly tipo: 'recolha' | 'largada';
  readonly data: string;
  readonly hora: string;
  readonly localidade: string;
  readonly voo: string;
  readonly horaPresenca: string;
  readonly margemH: string;
}

export interface Pedido {
  readonly id: string;
  readonly plano: string;
  readonly clienteSigla: string;
  readonly clienteNome: string;
  readonly itinerario: string;
  readonly referencia: string;
  readonly dataInicio: string;
  readonly dataFim: string;
  readonly dia0: number;
  readonly dia1: number;
  readonly lotacao: number;
  readonly requisitos: string;
  readonly estado: EstadoPedido;
  readonly urgente: boolean;
  readonly activo: boolean;
  readonly prioridade: number;
  readonly duvida: boolean;
  readonly origem: string;
  readonly natureza: string;
  readonly recolha: PontoPedido | null;
  readonly largada: PontoPedido | null;
}

/**
 * Rótulo e papel de cor de cada estado.
 *
 * ⚠️ **Os papéis são os da paleta validada** (`bom`, `aviso`, `grave`) e não cores
 * escolhidas aqui. A regra do sistema de cor é: **quente é problema**, verde é
 * conforme, frio e púrpura são clientes. Inventar uma cor nova para um estado punha
 * um tom quente a competir com o alarme — e a paleta foi validada contra isso.
 */
export const ESTADOS: Readonly<
  Record<EstadoPedido, { readonly rotulo: string; readonly papel: 'bom' | 'aviso' | 'grave' | 'neutro' }>
> = {
  recebido: { rotulo: 'recebido', papel: 'neutro' },
  em_validacao: { rotulo: 'em validação', papel: 'neutro' },
  aguarda_informacao: { rotulo: 'aguarda informação', papel: 'aviso' },
  aguarda_aprovacao: { rotulo: 'aguarda aprovação', papel: 'aviso' },
  novo: { rotulo: 'novo', papel: 'bom' },
  reservado: { rotulo: 'reservado', papel: 'aviso' },
  confirmado: { rotulo: 'confirmado', papel: 'bom' },
  executado: { rotulo: 'executado', papel: 'neutro' },
  cancelado: { rotulo: 'cancelado', papel: 'grave' },
};

/**
 * A ordem da fila de trabalho.
 *
 * 📗 *«O planeador pode dar prioridades aos pedidos de 0 a 10 para ordenar prioridades
 * de planeamento e subir ou descer na lista a ordem pela qual vai planear os pedidos.»*
 *
 * ⚠️ **Urgente não é prioridade 10.** A fonte lista `urgente` junto dos estados, mas
 * trata-o como marcador — e um pedido pode ser urgente com prioridade média: é o
 * telefonema que entra a meio da manhã. Se fosse prioridade, não se podia distinguir
 * «importante» de «agora».
 *
 * Entre dois igualmente prioritários decide a **data de início**: é o mais próximo que
 * arde.
 */
export function ordenarFila(pedidos: readonly Pedido[]): readonly Pedido[] {
  return [...pedidos].sort(
    (a, b) =>
      Number(b.urgente) - Number(a.urgente) ||
      b.prioridade - a.prioridade ||
      a.dataInicio.localeCompare(b.dataInicio),
  );
}

/**
 * O pedido está pronto a planear?
 *
 * 📗 Só o estado `novo` é «validado e pronto a planear» (5.3.1: aparece no dashboard
 * como «Novo» **após validação humana**). Os outros estão à espera de alguém.
 *
 * ⚠️ Um pedido `inactivo` também não: 📗 `inactivo` é «fora da lista de trabalho, sem
 * estar cancelado». Planear um pedido que alguém pôs de lado desfazia essa intenção.
 */
export function prontoAPlanear(p: Pedido): boolean {
  return p.estado === 'novo' && p.activo;
}

/** Porque é que este pedido não pode ser planeado agora. `null` se puder. */
export function razaoParaNaoPlanear(p: Pedido): string | null {
  if (!p.activo) return 'está inactivo — foi posto de lado sem ser cancelado';
  if (p.estado !== 'novo') {
    return `está em «${ESTADOS[p.estado].rotulo}» — só se planeia depois de validado`;
  }
  if (p.recolha === null || p.recolha.localidade === '') {
    // Sem local de recolha o motor não tem destino para onde mobilizar. É a lacuna
    // que a fonte 5.3.2.2 prevê: «faltam informações relevantes».
    return 'não diz onde recolher o grupo — sem isso o motor não tem destino';
  }
  return null;
}

/** As tags que a fonte pede: «cores, tags e marcadores». */
export function tags(p: Pedido): readonly string[] {
  const t: string[] = [];
  if (p.urgente) t.push('urgente');
  if (!p.activo) t.push('inactivo');
  if (p.duvida) t.push('provisório');
  if (p.natureza === 'ao_abrigo_contrato') t.push('contrato');
  if (p.natureza === 'confirmacao_final') t.push('confirmação');
  if (p.natureza === 'cancelamento') t.push('cancelamento');
  if (p.origem === 'telefone') t.push('telefone');
  if (p.recolha?.voo !== undefined && p.recolha.voo !== '') t.push('voo');
  return t;
}

/** Sobe ou desce na lista, dentro de 0 a 10. */
export function comPrioridade(p: Pedido, delta: number): Pedido {
  return { ...p, prioridade: Math.max(0, Math.min(10, p.prioridade + delta)) };
}
